// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Loader2, Ban, KeyRound, PauseCircle, Play } from 'lucide-react'
import * as api from '../../api/client'
import { useServerFunnels, type ServerFunnelColumn } from '../../components/common/TableFunnels'

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'

/**
 * Platform oversight of ONE pharmacy's Data API (owner, 2026-09-18: "i should have full control as
 * well not just budgeting and nothing else i must be incontroll").
 *
 * The platform had exactly one Data API endpoint — limits — against twenty on the pharmacy side it
 * could not reach at all, because the pharmacy guard requires the actor's own pharmacy context.
 *
 * THE BOUNDARY: full read, plus the three levers that STOP something — suspend a client, revoke a
 * key, pause a webhook. There is deliberately no platform "activate", "mint" or "grant": turning a
 * partner on, or widening what it sees, is a minimum-necessary decision (§164.502(b)) the pharmacy
 * signed a BAA for. The platform owns the kill switch; the pharmacy owns the scope.
 */
export function AdminOverviewPanel({ pharmacyId, onError }: { pharmacyId: string; onError: (e: string) => void }) {
  const [d, setD] = useState<api.AdminDataApiDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  // The per-client commercial limits are edited HERE and nowhere else (owner, 2026-09-18). The
  // pharmacy sees them read-only, and the pharmacy route ignores them even if called directly.
  const [editLimits, setEditLimits] = useState<string | null>(null)
  const [lf, setLf] = useState({ rate: '', quota: '', page: '', timeout: '' })

  const load = () => {
    setLoading(true)
    api.getAdminDataApiDetail(pharmacyId)
      .then(setD).catch(e => onError(e?.body?.message || e?.message || 'Could not load partners'))
      .finally(() => setLoading(false))
  }
  useEffect(() => { if (pharmacyId) load() }, [pharmacyId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function act(target: 'clients' | 'keys' | 'hooks', id: string, action: api.AdminDataApiAction, what: string, danger = false) {
    // The reason is REQUIRED by the server and lands in the pharmacy's audit trail. A platform
    // operator reaching into someone else's tenant should have to say why, every time.
    // Revoke is the one action that cannot be undone, so it says so BEFORE the operator
    // commits rather than in a toast afterwards.
    const warn = danger ? '\n\nTHIS CANNOT BE UNDONE — a revoked key can never be re-enabled.' : ''
    const reason = prompt(`${action[0].toUpperCase() + action.slice(1)} ${what}.${warn}\n\nThis is a platform intervention and is recorded in the pharmacy's audit trail.\n\nReason:`)
    if (!reason?.trim()) return
    setBusy(id); setNotice(null)
    try {
      await api.adminDataApiAction(pharmacyId, target, id, action, reason.trim())
      setNotice(`${what} — ${action}d.`)
      load()
    } catch (e: any) { onError(e?.body?.message || e?.message || `Could not ${action}`) }
    finally { setBusy(null) }
  }

  async function saveLimits(clientId: string, clear = false) {
    setBusy(clientId); setNotice(null)
    const n = (v: string) => (v.trim() === '' ? undefined : Number(v))
    try {
      await api.setAdminClientLimits(pharmacyId, clientId, clear
        ? { clearAll: true }
        : { rateLimitPerMin: n(lf.rate), dailyQuota: n(lf.quota), maxPageSize: n(lf.page), queryTimeoutSec: n(lf.timeout) })
      setNotice(clear ? 'Limits reset to the platform defaults.' : 'Limits saved.')
      setEditLimits(null)
      load()
    } catch (e: any) {
      const b = e?.body
      onError(b?.field ? `${b.field}: ${b.message}` : (b?.message || e?.message || 'Could not save limits'))
    } finally { setBusy(null) }
  }

  if (loading) return <p style={{ color: MUTE, fontSize: 13 }}><Loader2 size={14} className="spin" aria-hidden="true" /> Loading…</p>
  if (!d) return null

  const clientName = (id: string) => d.clients.find(c => c.id === id)?.name ?? '(unknown)'

  return (
    <>
      {notice && <div role="status" style={banner('#dcfce7', '#15803d')}>{notice}</div>}

      <section aria-labelledby="ad-clients" style={{ marginBottom: 22 }}>
        <h3 id="ad-clients" style={h3}>Partners ({d.clients.length})</h3>
        {d.clients.length === 0 ? <Empty>No Data API partners for this pharmacy.</Empty> : (
          <Table caption="Every Data API partner configured by this pharmacy.">
            <thead><tr>
              <th scope="col" style={th}>Partner</th><th scope="col" style={th}>Status</th>
              <th scope="col" style={th}>BAA</th><th scope="col" style={th}>Cert</th>
              <th scope="col" style={thNum}>Keys</th><th scope="col" style={thNum}>Fields</th>
              <th scope="col" style={thNum}>Hooks</th><th scope="col" style={th}>Limits</th><th scope="col" style={th}></th>
            </tr></thead>
            <tbody>
              {d.clients.map(c => (
                <tr key={c.id}>
                  <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                    {c.name}
                    {c.purpose && <div style={{ fontWeight: 400, fontSize: 11, color: MUTE }}>{c.purpose}</div>}
                  </th>
                  <td style={td}><Pill v={c.status} /></td>
                  <td style={td}>{c.baa_ref ?? <span style={{ color: '#b91c1c' }}>none</span>}</td>
                  <td style={td}>{c.cert_pinned ? 'pinned' : <span style={{ color: '#b91c1c' }}>none</span>}</td>
                  <td style={tdNum}>{c.live_keys}</td>
                  <td style={tdNum}>{c.grants}</td>
                  <td style={tdNum}>{c.hooks}</td>
                  <td style={{ ...td, fontSize: 11, color: MUTE, whiteSpace: 'nowrap' }}>
                    {c.rate_limit_per_min ?? 60}/min · {(c.daily_quota ?? 10000).toLocaleString()}/day
                    <br />{(c.max_page_size ?? 1000).toLocaleString()} rows · {c.query_timeout_sec ?? 30}s
                    <br /><button type="button" style={{ ...btn, padding: '2px 7px', fontSize: 11, marginTop: 3 }}
                      onClick={() => {
                        setEditLimits(editLimits === c.id ? null : c.id)
                        setLf({
                          rate: c.rate_limit_per_min?.toString() ?? '',
                          quota: c.daily_quota?.toString() ?? '',
                          page: c.max_page_size?.toString() ?? '',
                          timeout: c.query_timeout_sec?.toString() ?? '',
                        })
                      }}>{editLimits === c.id ? 'Close' : 'Edit limits'}</button>
                  </td>
                  <td style={td}>
                    {c.status === 'active' ? (
                      <button type="button" style={btnDanger} disabled={busy === c.id}
                        onClick={() => act('clients', c.id, 'suspend', c.name)}>
                        <Ban size={12} aria-hidden="true" /> Suspend
                      </button>
                    ) : (
                      <button type="button" style={btn} disabled={busy === c.id}
                        onClick={() => act('clients', c.id, 'activate', c.name)}
                        title={c.baa_ref ? undefined : 'Blocked: no BAA reference on file'}>
                        <Play size={12} aria-hidden="true" /> Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {editLimits && (
                <tr>
                  <td colSpan={9} style={{ ...td, background: 'var(--color-bg-soft, #fafafa)' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                      Limits — {d.clients.find(c => c.id === editLimits)?.name}
                    </div>
                    <div style={{ fontSize: 11, color: MUTE, marginBottom: 8 }}>
                      Blank means the platform default. A value can only NARROW the default, never exceed it —
                      the runtime clamps it either way, so a larger number would store something that does nothing.
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <LimitInput id="lf-rate" label="Rate / min" v={lf.rate} on={v => setLf({ ...lf, rate: v })} dflt="60" />
                      <LimitInput id="lf-quota" label="Daily quota" v={lf.quota} on={v => setLf({ ...lf, quota: v })} dflt="10000" />
                      <LimitInput id="lf-page" label="Max rows / page" v={lf.page} on={v => setLf({ ...lf, page: v })} dflt="1000" />
                      <LimitInput id="lf-to" label="Query timeout (s)" v={lf.timeout} on={v => setLf({ ...lf, timeout: v })} dflt="30" />
                      <button type="button" style={btn} disabled={busy === editLimits}
                        onClick={() => saveLimits(editLimits)}>Save limits</button>
                      <button type="button" style={btn} disabled={busy === editLimits}
                        onClick={() => saveLimits(editLimits, true)}>Reset to defaults</button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </section>

      <section aria-labelledby="ad-keys" style={{ marginBottom: 22 }}>
        <h3 id="ad-keys" style={h3}>API keys ({d.keys.filter(k => !k.revoked).length} live)</h3>
        {d.keys.length === 0 ? <Empty>No keys minted.</Empty> : (
          <Table caption="API keys across every partner. The key itself is never stored or shown — only its prefix.">
            <thead><tr>
              <th scope="col" style={th}>Prefix</th><th scope="col" style={th}>Partner</th>
              <th scope="col" style={th}>Created</th><th scope="col" style={th}>Last used</th>
              <th scope="col" style={th}>State</th><th scope="col" style={th}></th>
            </tr></thead>
            <tbody>
              {d.keys.map(k => (
                <tr key={k.id}>
                  <th scope="row" style={{ ...td, textAlign: 'left', fontFamily: 'monospace' }}>{k.key_prefix}…</th>
                  <td style={td}>{clientName(k.client_id)}</td>
                  <td style={td}>{fmt(k.created_at)}</td>
                  <td style={td}>{k.last_used_at ? fmt(k.last_used_at) : <span style={{ color: MUTE }}>never</span>}</td>
                  <td style={td}><Pill v={k.revoked ? 'revoked' : k.active ? 'active' : 'inactive'} /></td>
                  <td style={td}>
                    {!k.revoked && (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        {k.active
                          ? <button type="button" style={btn} disabled={busy === k.id}
                              onClick={() => act('keys', k.id, 'disable', `key ${k.key_prefix}…`)}>Disable</button>
                          : <button type="button" style={btn} disabled={busy === k.id}
                              onClick={() => act('keys', k.id, 'enable', `key ${k.key_prefix}…`)}>Enable</button>}
                        <button type="button" style={btnDanger} disabled={busy === k.id}
                          onClick={() => act('keys', k.id, 'revoke', `key ${k.key_prefix}…`, true)}>
                          <KeyRound size={12} aria-hidden="true" /> Revoke
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>

      <section aria-labelledby="ad-hooks">
        <h3 id="ad-hooks" style={h3}>Webhooks ({d.hooks.length})</h3>
        {d.hooks.length === 0 ? <Empty>No webhooks configured.</Empty> : (
          <Table caption="Outbound webhooks and their current delivery health.">
            <thead><tr>
              <th scope="col" style={th}>Hook</th><th scope="col" style={th}>Partner</th>
              <th scope="col" style={th}>Status</th><th scope="col" style={thNum}>Fails</th>
              <th scope="col" style={th}>Last error</th><th scope="col" style={th}></th>
            </tr></thead>
            <tbody>
              {d.hooks.map(h => (
                <tr key={h.id}>
                  <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 600 }}>
                    {h.name}
                    <div style={{ fontWeight: 400, fontSize: 11, color: MUTE, wordBreak: 'break-all' }}>{h.url}</div>
                  </th>
                  <td style={td}>{clientName(h.client_id)}</td>
                  <td style={td}><Pill v={h.status} /></td>
                  <td style={tdNum}>{h.consecutive_failures}</td>
                  <td style={{ ...td, maxWidth: 220, fontSize: 11 }}>{h.last_error ?? '—'}</td>
                  <td style={td}>
                    {h.status === 'active' ? (
                      <button type="button" style={btnDanger} disabled={busy === h.id}
                        onClick={() => act('hooks', h.id, 'pause', h.name)}>
                        <PauseCircle size={12} aria-hidden="true" /> Pause
                      </button>
                    ) : h.status === 'paused' ? (
                      <button type="button" style={btn} disabled={busy === h.id}
                        onClick={() => act('hooks', h.id, 'resume', h.name)}>
                        <Play size={12} aria-hidden="true" /> Resume
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </section>
    </>
  )
}

/** §164.528 accounting for the whole pharmacy. Server-paged — six-year retention makes this the
 *  largest table on the surface. */
// D-UI-2: the funnels of the disclosures list. Server-paged, so they are sent to the endpoint
// (`filter=`), never applied to the page on screen. Keys match the endpoint's allow-list.
const DISCLOSURE_FUNNEL_COLS: ServerFunnelColumn[] = [
  { key: 'requested_at', label: 'When', kind: 'date' },
  { key: 'client_name', label: 'Partner', kind: 'text' },
  { key: 'resource', label: 'Resource', kind: 'text' },
  { key: 'row_count', label: 'Rows', kind: 'number' },
  { key: 'patient_count', label: 'Patients', kind: 'number' },
  { key: 'status', label: 'Status', kind: 'number' },
]

export function AdminDisclosuresPanel({ pharmacyId, onError }: { pharmacyId: string; onError: (e: string) => void }) {
  const [rows, setRows] = useState<api.AdminDisclosureRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const PAGE = 50
  const fx = useServerFunnels(DISCLOSURE_FUNNEL_COLS)
  // A range funnel refetches as it is typed; only the newest request may land.
  const reqSeq = useRef(0)

  const fetchPage = (p: number) => {
    const seq = ++reqSeq.current
    setLoading(true)
    api.getAdminDataApiDisclosures(pharmacyId, { page: p, page_size: PAGE, filter: fx.filter })
      .then(r => { if (seq !== reqSeq.current) return; setRows(r.disclosures); setTotal(r.total); setPage(r.page) })
      .catch(e => { if (seq === reqSeq.current) onError(e?.body?.message || e?.message || 'Could not load disclosures') })
      .finally(() => { if (seq === reqSeq.current) setLoading(false) })
  }
  useEffect(() => { if (pharmacyId) fetchPage(0) }, [pharmacyId, fx.sig]) // eslint-disable-line react-hooks/exhaustive-deps

  const pageCount = Math.max(1, Math.ceil(total / PAGE))

  // While a funnel is set the table stays mounted, loading or empty: the header is where the
  // funnel is cleared, and unmounting it would close the funnel being typed in.
  if (loading && rows.length === 0 && !fx.active) return <p style={{ color: MUTE, fontSize: 13 }}><Loader2 size={14} className="spin" aria-hidden="true" /> Loading…</p>

  return (
    <>
      <p style={{ fontSize: 12, color: MUTE, margin: '0 0 10px' }}>
        Every disclosure of PHI to a partner, under 45 CFR §164.528. Retained six years (§164.530(j))
        and never purged — this is the record a patient can ask for.
      </p>
      {rows.length === 0 && !fx.active ? <Empty>No disclosures recorded.</Empty> : (
        <>
          {fx.chips}
          <Table caption="Disclosures to Data API partners for this pharmacy." tableProps={fx.tableProps}>
            <thead><tr>
              <th scope="col" style={th}>When{fx.funnel('requested_at')}</th><th scope="col" style={th}>Partner{fx.funnel('client_name')}</th>
              <th scope="col" style={th}>Resource{fx.funnel('resource')}</th><th scope="col" style={thNum}>Rows{fx.funnel('row_count')}</th>
              <th scope="col" style={thNum}>Patients{fx.funnel('patient_count')}</th><th scope="col" style={thNum}>Status{fx.funnel('status')}</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} style={{ ...td, color: MUTE }}>{loading ? 'Loading…' : 'No disclosures match these filters.'}</td></tr>}
              {rows.map((r, i) => (
                <tr key={i}>
                  <th scope="row" style={{ ...td, textAlign: 'left', fontWeight: 400 }}>{fmt(r.requested_at)}</th>
                  <td style={td}>{r.client_name ?? '(deleted)'}</td>
                  <td style={td}>{r.resource}</td>
                  <td style={tdNum}>{r.row_count}</td>
                  <td style={tdNum}>{r.patient_count}</td>
                  <td style={tdNum}>{r.status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {total > PAGE && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
              <button type="button" style={btn} disabled={page === 0 || loading} onClick={() => fetchPage(page - 1)}>Previous</button>
              <span style={{ fontSize: 12, color: MUTE }} aria-live="polite">Page {page + 1} of {pageCount} · {total} disclosures</span>
              <button type="button" style={btn} disabled={page >= pageCount - 1 || loading} onClick={() => fetchPage(page + 1)}>Next</button>
            </div>
          )}
        </>
      )}
    </>
  )
}

// ── small shared pieces ─────────────────────────────────────────────────────────────────────────

function LimitInput({ id, label, v, on, dflt }: { id: string; label: string; v: string; on: (s: string) => void; dflt: string }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{label}</label>
      <input id={id} type="number" min={1} value={v} onChange={e => on(e.target.value)} placeholder={dflt}
        style={{ width: 120, padding: '5px 8px', border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 12 }} />
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p style={{ color: MUTE, fontSize: 13, margin: 0 }}>{children}</p>
}

/** `tableProps` is optional and only the server-paged disclosures list passes it (its own
 *  funnels); the panels that hold their full arrays pass nothing and render as before. */
function Table({ caption, children, tableProps }: { caption: string; children: React.ReactNode; tableProps?: { 'data-funnels': 'own' } }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table {...tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <caption style={{ captionSide: 'bottom', textAlign: 'left', fontSize: 11, color: MUTE, paddingTop: 6 }}>{caption}</caption>
        {children}
      </table>
    </div>
  )
}

function Pill({ v }: { v: string }) {
  const tone = v === 'active' ? ['#dcfce7', '#15803d']
    : v === 'revoked' || v === 'suspended' ? ['#fee2e2', '#b91c1c']
    : v === 'paused' ? ['#fef3c7', '#b45309'] : ['#f1f5f9', '#475569']
  return <span style={{ background: tone[0], color: tone[1], fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' }}>{v}</span>
}

const fmt = (iso: string) => new Date(iso).toLocaleString()

const h3: CSSProperties = { fontSize: 14, margin: '0 0 8px', fontWeight: 700 }
const th: CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: `1px solid ${BORDER}`, fontSize: 11, textTransform: 'uppercase', color: MUTE, whiteSpace: 'nowrap' }
const thNum: CSSProperties = { ...th, textAlign: 'right' }
const td: CSSProperties = { padding: '6px 8px', borderBottom: `1px solid ${BORDER}`, verticalAlign: 'top' }
const tdNum: CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const btn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', border: `1px solid ${BORDER}`, borderRadius: 6, background: 'transparent', fontSize: 12, cursor: 'pointer' }
const btnDanger: CSSProperties = { ...btn, color: 'var(--color-danger, #b91c1c)', borderColor: 'var(--color-danger, #b91c1c)' }

function banner(bg: string, fg: string): CSSProperties {
  return { background: bg, color: fg, padding: '8px 12px', borderRadius: 7, fontSize: 13, marginBottom: 14 }
}
